import type { TextFieldProps } from '@mui/material/TextField';
import type { AutocompleteProps } from '@mui/material/Autocomplete';

import { Controller, useFormContext } from 'react-hook-form';

import { Box } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// ----------------------------------------------------------------------

export type AutocompleteBaseProps = Omit<
  AutocompleteProps<any, boolean, boolean, boolean>,
  'renderInput'
>;

export type RHFAutocompleteProps = AutocompleteBaseProps & {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  endAdornment?: any;
  slotProps?: AutocompleteBaseProps['slotProps'] & {
    textfield?: TextFieldProps;
  };
  onChangeField?: ({ value, fieldName }: { value: string; fieldName: string }) => void;
};

export function RHFAutocomplete({
  name,
  label,
  slotProps,
  helperText,
  placeholder,
  endAdornment,
  onChangeField,
  ...other
}: RHFAutocompleteProps) {
  const { control, setValue } = useFormContext();

  const { textfield, ...otherSlotProps } = slotProps ?? {};

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Autocomplete
            {...field}
            id={`rhf-autocomplete-${name}`}
            onChange={(event, newValue) => {
              setValue(name, newValue, { shouldValidate: true });
              if (onChangeField) onChangeField({ value: newValue, fieldName: name });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...textfield}
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error?.message ?? helperText}
                slotProps={{
                  ...textfield?.slotProps,
                  htmlInput: {
                    ...params.inputProps,
                    autoComplete: 'new-password',
                    ...textfield?.slotProps?.htmlInput,
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  ...textfield?.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {endAdornment}
                    </>
                  ),
                }}
              />
            )}
            slotProps={{ ...otherSlotProps }}
            sx={{ width: '100%' }}
            {...other}
          />
        </Box>
      )}
    />
  );
}
